import { z } from 'zod';
import {
    type PagedResult,
    type SortablePageParams,
    metaSchema,
    recordSchema,
    sortablePageParamsSchema
} from '#api/common.ts';
import {
    AccountType,
    CreditorClassification,
    AutopayAmountBasis,
    AutopayBehavior,
    CreditReportingMode,
    Frequency,
    LedgerType,
    PaymentCapStrategy,
    PaymentDueDayInvalidBehavior,
    PaymentDueDayRange,
    PortfolioType,
    ProductType,
    SalesTaxStrategy
} from '#lib/models/enums.ts';
import { GenericApi } from './genericApi';
import { functionArgFormSchema } from './scriptApi';

export const ledgerDefinitionSchema = z.object({
    ledgerType: LedgerType.default('Balance'),
    code: z.string().default(''),
    name: z.string(),
    description: z.string().optional(),
    excludePaymentSpread: z.boolean().optional(),
    isInherited: z.boolean().optional(),
    meta: metaSchema.optional()
});

export const createLedgerSchema = z.object({
    ledgerType: LedgerType.default('Balance'),
    code: z.string().min(1, { message: 'Code is required' }),
    name: z.string().min(1, { message: 'Name is required' }),
    description: z.string().optional(),
    excludePaymentSpread: z.boolean().optional(),
    meta: metaSchema.optional()
});

export type CreateLedger = z.infer<typeof createLedgerSchema>;

export const PaymentSpreadStrategy = z.enum(['CustomScript', 'AlternatingFees', 'PastDueThenFees', 'Flex']);
export type PaymentSpreadStrategy = z.infer<typeof PaymentSpreadStrategy>;

// Free-form dictionary — the UI and the payment-spread script own the shape.
// Conventional keys today: `description` (for CustomScript), `order` (Flex), `feePriority` (any non-CustomScript).
export const paymentSpreadConfigSchema = z.record(z.string(), z.unknown()).optional();
export type PaymentSpreadConfig = z.infer<typeof paymentSpreadConfigSchema>;

// Per-product remapping of directly-sourced credit-reporting tradeline fields.
// Keys are field names; an absent entry means the field uses its built-in default source.
export const CreditReportingField = z.enum([
    'OpenDate',
    'CreditLimit',
    'OriginalLoanAmount',
    'ScheduledMonthlyPayment',
    'CurrentBalance'
]);
export type CreditReportingField = z.infer<typeof CreditReportingField>;

export const CreditReportingSourceKind = z.enum(['Native', 'DataKey']);
export type CreditReportingSourceKind = z.infer<typeof CreditReportingSourceKind>;

export const creditReportingFieldSourceSchema = z.object({
    kind: CreditReportingSourceKind,
    value: z.string()
});
export type CreditReportingFieldSource = z.infer<typeof creditReportingFieldSourceSchema>;

// Keyed by CreditReportingField name; only remapped fields are present (others use their default).
export const creditReportingFieldMapSchema = z.object({
    fields: z.record(z.string(), creditReportingFieldSourceSchema).default({})
});
export type CreditReportingFieldMap = z.infer<typeof creditReportingFieldMapSchema>;

export const productSchema = z.object({
    id: z.coerce.number(),
    name: z.string(),
    code: z.string(),
    clientId: z.coerce.number(),
    clientName: z.string().optional(),
    portfolioType: PortfolioType.default('Installment'),
    accountType: AccountType.default('HomeImprovement'),
    originalCreditorName: z.string().max(30).default(''),
    creditorClassification: CreditorClassification.default('Financial'),
    productType: ProductType.default(ProductType.enum.HomeImprovementLoan),
    paymentCapStrategy: PaymentCapStrategy.default('None'),
    autopayBehavior: AutopayBehavior.default('Optional'),
    autopayAmountBasis: AutopayAmountBasis.default('ScheduledPayment'),
    promptAutopayEnrollment: z.boolean().default(false),
    hasPayoff: z.boolean().default(false),
    creditReportingMode: CreditReportingMode.default('None'),
    isCollectable: z.boolean().default(false),
    paymentDueDayRange: PaymentDueDayRange.default('Days1To28'),
    paymentDueDayInvalidBehavior: PaymentDueDayInvalidBehavior.default('LastDayOfMonth'),
    termFrequency: Frequency.default('Monthly'),
    shareObligorData: z.boolean().default(false),
    allowLenderChange: z.boolean().default(false),
    salesTaxStrategies: z
        .array(SalesTaxStrategy)
        .refine((v) => v.length <= 1 || (!v.includes('None') && !v.includes('Combined')), {
            message: '"None" and "Combined" can’t be selected with other sales tax rates'
        })
        .default([]),
    documentLogoUrl: z.string().default(''),
    updatedUtc: z.coerce.date().optional(),
    createdUtc: z.coerce.date().optional(),
    ledgers: z.array(ledgerDefinitionSchema).optional(),
    data: z.union([recordSchema, z.null()]).optional(),
    meta: metaSchema.optional(),
    notifyOnReturnFailureUserId: z.number().nullable().optional(),
    inheritedProductId: z.number().nullable().optional(),
    inheritedProductName: z.string().nullable().optional(),
    paymentSpreadStrategy: PaymentSpreadStrategy.optional(),
    paymentSpreadConfig: paymentSpreadConfigSchema,
    labels: z.record(z.string(), z.string()).optional(),
    creditReportingFieldMap: creditReportingFieldMapSchema.optional()
});

export const spreadOperationDetailSchema = z.object({
    name: z.string(),
    repeatable: z.boolean().default(false),
    argsForm: functionArgFormSchema.nullish()
});

export const spreadOperationCatalogSchema = z.object({
    productId: z.coerce.number(),
    operations: z.array(z.string()).default([]),
    operationDetails: z.array(spreadOperationDetailSchema).default([]),
    feeCodes: z.array(z.string()).default([]),
    defaultOrder: z.array(z.string()).default([]),
    defaultFeePriority: z.array(z.string()).default([]),
    strategies: z.array(z.string()).default([]),
    defaultStrategy: z.string().nullable().optional(),
    strategiesSupportingOrder: z.array(z.string()).default([])
});

export type SpreadOperationDetail = z.infer<typeof spreadOperationDetailSchema>;
export type SpreadOperationCatalog = z.infer<typeof spreadOperationCatalogSchema>;

// A Flex `order` entry: a bare op name, or an object entry carrying op args
// (e.g. { op: 'payFees', subcode: 'LCA', maxFees: 2 }).
export type SpreadOrderEntry = string | ({ op: string } & Record<string, unknown>);

export const createProductSchema = z.object({
    name: z.string().min(1, {
        message: 'Name is required'
    }),
    code: z.string().length(4, {
        message: 'Code must be 4 characters'
    }),
    clientId: z.coerce.number(),
    portfolioType: PortfolioType.default('Installment'),
    accountType: AccountType.default('HomeImprovement'),
    originalCreditorName: z.string().max(30).optional(),
    creditorClassification: CreditorClassification.optional(),
    productType: ProductType.default(ProductType.enum.HomeImprovementLoan),
    // Optional: omit to let the server derive these from productType (soft default).
    paymentCapStrategy: PaymentCapStrategy.optional(),
    autopayAmountBasis: AutopayAmountBasis.optional(),
    hasPayoff: z.boolean().optional(),
    autopayBehavior: AutopayBehavior.default('Optional'),
    promptAutopayEnrollment: z.boolean().default(false),
    creditReportingMode: CreditReportingMode.default('None'),
    isCollectable: z.boolean().default(false),
    paymentDueDayRange: PaymentDueDayRange.default('Days1To28'),
    paymentDueDayInvalidBehavior: PaymentDueDayInvalidBehavior.default('LastDayOfMonth'),
    termFrequency: Frequency.optional(),
    shareObligorData: z.boolean().default(false),
    allowLenderChange: z.boolean().default(false),
    salesTaxStrategies: z.array(SalesTaxStrategy).optional(),
    documentLogoUrl: z.string().optional(),
    ledgers: z.array(ledgerDefinitionSchema).optional(),
    data: z.union([recordSchema, z.null()]).optional(),
    meta: metaSchema.optional(),
    notifyOnReturnFailureUserId: z.number().nullable().optional(),
    inheritedProductId: z.number().nullable().optional(),
    paymentSpreadStrategy: PaymentSpreadStrategy.optional(),
    paymentSpreadConfig: paymentSpreadConfigSchema,
    labels: z.record(z.string(), z.string()).optional(),
    creditReportingFieldMap: creditReportingFieldMapSchema.optional()
});

export const updateProductSchema = createProductSchema
    .omit({ clientId: true, code: true })
    .extend({
        clearInheritedProductId: z.boolean().optional(),
        // Loan-only product data field (key `calculateRemainingTerm`); server materializes it on enable.
        calculateRemainingTerm: z.boolean().optional(),
        // Late-fee-waiver caps — product data fields the server materializes on demand.
        maxLcwPerPeriod: z.number().optional(),
        maxRtwPerPeriod: z.number().optional(),
        waiverPeriodMonths: z.number().optional()
    })
    .partial();

export const listProductSchema = sortablePageParamsSchema.extend({
    clientId: z.number().optional(),
    name: z.string().optional()
});

export const userProductSchema = z.object({
    productId: z.number(),
    name: z.string().optional().default(''),
    code: z.string(),
    hasAccess: z.boolean().default(false)
});

export const productPermissionSchema = z.object({
    productId: z.number().optional(),
    hasAccess: z.boolean().default(false)
});

export const updateUserProductsSchema = z.object({
    userId: z.number().optional(),
    clientId: z.number().optional(),
    permissions: z.array(productPermissionSchema)
});

export const listUserProductsSchema = sortablePageParamsSchema.extend({
    userId: z.number().optional(),
    clientId: z.number().optional()
});

export type Product = z.infer<typeof productSchema>;
export type CreateProduct = z.infer<typeof createProductSchema>;
export type UpdateProduct = z.infer<typeof updateProductSchema>;
export type ListProduct = z.infer<typeof listProductSchema>;
export type LedgerDefinition = z.infer<typeof ledgerDefinitionSchema>;

export type UserProduct = z.infer<typeof userProductSchema>;
export type ListUserProducts = z.infer<typeof listUserProductsSchema>;
export type ProductPermission = z.infer<typeof productPermissionSchema>;
export type UpdateUserProducts = z.infer<typeof updateUserProductsSchema>;

export class ProductApi extends GenericApi<Product, CreateProduct, UpdateProduct, ListProduct> {
    constructor() {
        super('products');
    }

    async listGlobal() {
        return await this.fetch.getJSON<PagedResult<Product>>(`${this.route}/global`);
    }

    async listUserProducts(params: ListUserProducts) {
        let url = `${this.route}/access`;

        if (params.userId) {
            url = `${url}/${params.userId}`;
        } else {
            params.userId = undefined;
        }

        return await this.fetch.getJSON<PagedResult<UserProduct>>(url, { params });
    }

    async updateUserProducts(payload: UpdateUserProducts) {
        return await this.fetch.putJSON<PagedResult<UserProduct>>(`${this.route}/access/${payload.userId}`, payload);
    }

    async listInheriting(productId: number, params?: SortablePageParams) {
        return await this.fetch.getJSON<PagedResult<Product>>(`${this.route}/${productId}/inheriting`, { params });
    }

    async getSpreadCatalog(productId: number) {
        return await this.fetch.getJSON<SpreadOperationCatalog>(`${this.route}/${productId}/spread-operations`);
    }

    async syncLedgers(productId: number) {
        return await this.fetch.postJSON<{ trackingId: string; message: string }>(
            `${this.route}/${productId}/sync-ledgers`,
            {}
        );
    }

    async getLedgerSyncStatus(productId: number) {
        return await this.fetch.getJSON<LedgerSyncStatus>(`${this.route}/${productId}/sync-ledgers/status`);
    }
}

export interface LedgerSyncEntry {
    productId: number;
    name: string;
    clientId: number;
    pendingLedgers: string[];
    configOnlyLedgers: string[];
    missingObligationRows: number;
    missingRowsByCode?: Record<string, number>;
}

export interface LedgerSyncStatus {
    children: number;
    outOfSync: number;
    outOfSyncChildren: LedgerSyncEntry[];
    self?: LedgerSyncEntry | null;
}
